import { SeasonType, StageType } from '@/model';
import { PostStagesBatchItemRequest } from '@/services/request/stage';
import { SeasonFormData } from '../schemas';

/** Normaliza um nome de zona: vazio/whitespace → undefined (backend mantém NULL = default). */
const cleanLabel = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * Constrói o payload `items` para POST /stages/batch a partir do form do wizard.
 *
 * Regras:
 * - LEAGUE   → 1 item type=LEAGUE, name="Fase única", stage_order=1.
 * - KNOCKOUT → 1 item type=KNOCKOUT por knockout_stages, stage_order começa em 1.
 * - HYBRID   → group_stages (type=GROUP, name="Fase de Grupos") seguidos de
 *              knockout_stages (type=KNOCKOUT); `stage_order` é contínuo.
 *
 * Para GROUP, o backend força `name="Fase de Grupos"`, mas mandamos explicitamente
 * para manter o payload completo e legível.
 */
export const buildStagesBatchItems = (
  values: SeasonFormData,
): PostStagesBatchItemRequest[] => {
  if (values.season_type === SeasonType.LEAGUE) {
    const cfg = values.league_stage_config;
    return [
      {
        name: 'Fase única',
        type: StageType.LEAGUE,
        stage_order: 1,
        direct_qualifiers_count: cfg?.direct_qualifiers_count ?? 0,
        playoff_qualifiers_count: cfg?.playoff_qualifiers_count ?? 0,
        secondary_qualifiers_count: cfg?.secondary_qualifiers_count ?? 0,
        relegated_count: cfg?.relegated_count ?? 0,
        direct_qualifiers_label: cleanLabel(cfg?.direct_qualifiers_label),
        playoff_qualifiers_label: cleanLabel(cfg?.playoff_qualifiers_label),
        secondary_qualifiers_label: cleanLabel(cfg?.secondary_qualifiers_label),
        relegated_qualifiers_label: cleanLabel(cfg?.relegated_qualifiers_label),
      },
    ];
  }

  if (values.season_type === SeasonType.KNOCKOUT) {
    return values.knockout_stages.map((stage, index) => ({
      name: stage.name.trim(),
      type: StageType.KNOCKOUT,
      stage_order: index + 1,
    }));
  }

  if (values.season_type === SeasonType.HYBRID) {
    const groups: PostStagesBatchItemRequest[] = values.group_stages.map(
      (stage, index) => ({
        name: 'Fase de Grupos',
        group_name: stage.group_name.trim(),
        type: StageType.GROUP,
        stage_order: index + 1,
        direct_qualifiers_count: stage.direct_qualifiers_count ?? 0,
        playoff_qualifiers_count: stage.playoff_qualifiers_count ?? 0,
        secondary_qualifiers_count: stage.secondary_qualifiers_count ?? 0,
        relegated_count: stage.relegated_count ?? 0,
        direct_qualifiers_label: cleanLabel(stage.direct_qualifiers_label),
        playoff_qualifiers_label: cleanLabel(stage.playoff_qualifiers_label),
        secondary_qualifiers_label: cleanLabel(stage.secondary_qualifiers_label),
        relegated_qualifiers_label: cleanLabel(stage.relegated_qualifiers_label),
      }),
    );
    const knockouts: PostStagesBatchItemRequest[] = values.knockout_stages.map(
      (stage, index) => ({
        name: stage.name.trim(),
        type: StageType.KNOCKOUT,
        stage_order: groups.length + index + 1,
      }),
    );
    return [...groups, ...knockouts];
  }

  return [];
};
